import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

import { TextDetailsTuple } from '../text/textDetailsTuple';

const title = 'VPC';

interface VpcOption {
  id: string;
  name?: string;
}

export const VpcSelect = ({
  accountId,
  regionName,
  vpc,
  onChange,
  disabledItems,
}: {
  accountId?: string;
  regionName?: string;
  vpc?: string;
  onChange: (vpc: string) => void;
  disabledItems: string[];
}) => {
  if (!accountId || !regionName) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerVpcSelect
      accountId={accountId}
      regionName={regionName}
      vpc={vpc}
      onChange={onChange}
      disabledItems={disabledItems}
    />
  );
};

const InnerVpcSelect = ({
  accountId,
  regionName,
  vpc,
  onChange,
  disabledItems,
}: {
  accountId: string;
  regionName: string;
  vpc?: string;
  onChange: (vpc: string) => void;
  disabledItems: string[];
}) => {
  const dal = useDAL();
  const { data: body } = dal.restore.awsVpcs.useList(accountId, regionName);

  if (!body?.vpcs.length) {
    return <EmptySelect title={title} />;
  }

  const options: VpcOption[] = body.vpcs
    .map((v) => ({ id: v.id, name: v.name }))
    .sort((a, b) => a.id.localeCompare(b.id));

  const selectedVpc = options.find((o) => o.id === vpc) || null;

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        data-testid='vpcSelect'
        clearOnBlur
        size='small'
        value={selectedVpc}
        onChange={(event, newValue) => onChange(newValue?.id || '')}
        getOptionDisabled={(option) => disabledItems.includes(option.id)}
        getOptionLabel={(option) => option?.id || ''}
        filterOptions={(options, state) => {
          const input = state.inputValue.trim().toLowerCase();
          if (!input) return options;
          return options.filter((o) => {
            const id = o.id?.toLowerCase() ?? '';
            const name = o.name?.toLowerCase() ?? '';
            return id.includes(input) || name.includes(input);
          });
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <TextDetailsTuple
              primaryProp={option.id}
              secondaryProp={option.name || option.id}
              enableSameValue={true}
            />
          </li>
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder='Select'
              label=''
              slotProps={{ inputLabel: { shrink: true } }}
            />
          );
        }}
        options={options}
      />
    </FormControl>
  );
};
